import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'

import Reference = ents.core.dms.data.Value.Reference

/**
 * Displays resolved display names for DMS reference values.
 *
 * Handles all reference formats:
 * - **Entity single** (`{ entityId, recordId }`) → one tag
 * - **Entity array** (`[{ entityId, recordId }, ...]`) → multiple tags
 * - **Dimension single** (`{ '!campus': '?1', '!building': '?2' }`) →
 *   single tag with leaf record's breadcrumb
 * - **Dimension multi** (`{ '!campus': ['?1', '?2'] }`) → individual tags
 *
 * Uses the {@link state.display.referenceRecords.Feature} to load and resolve
 * reference records into human-readable names. Dispatches `loadRecords` with
 * `cacheFirst: true` so subsequent displays for the same entity skip the API.
 */
@ngCore.Component({
  imports: [ds.tag.Component],
  selector: 'atn-reference-readonly-value',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.display.referenceRecords.Feature} */
  private readonly _referenceRecordsFeature = ngCore.inject(
    state.display.referenceRecords.Feature,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** The raw reference value — Dimension, Entity, or array format */
  public readonly valueSg = ngCore.input.required<
    | Reference.Default
    | readonly Reference.Default[]
    | Reference.Multi.Default
    | Reference.InFormat.Dimension
  >({ alias: 'value' })

  /**
   * Normalizes the input value into resolved `{ entityId, recordId }` entries
   * with full `Entity.Id`s.
   *
   * For **dimension-single**, only the leaf entity is included — the leaf
   * record's `ancestors` field already carries parent names, so loading
   * parent entity records would be redundant.
   */
  private readonly _resolvedEntriesSg = ngCore.computed(
    (
      value = this.valueSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ): readonly Reference.Default[] => {
      if (!schema) {
        return []
      }

      // Entity format single: { entityId, recordId }
      if (Reference.Default.isDefault(value)) {
        if (!value.entityId || !value.recordId) {
          return []
        }
        const entityId = this._resolveEntityId(schema, value.entityId)
        return entityId ? [{ entityId, recordId: value.recordId }] : []
      }

      // Entity format array: [{ entityId, recordId }, ...]
      if (Array.isArray(value)) {
        return value
          .filter(Reference.Default.isDefault)
          .map(entry => {
            const entityId = this._resolveEntityId(schema, entry.entityId)
            return entityId ? { entityId, recordId: entry.recordId } : undefined
          })
          .filter(TypeChecker.isNotNullish.raw)
      }

      // Dimension multi: { '!campus': ['?1', '?2'], '!building': ['?3'] }
      if (Reference.Multi.Default.isDefault(value)) {
        const entries: Reference.Default[] = []
        for (const [rawEntityId, recordIds] of Object.entries(value)) {
          if (!recordIds) {
            continue
          }
          const entityId = this._resolveEntityId(schema, rawEntityId)
          if (!entityId) {
            continue
          }
          for (const rawRecordId of recordIds) {
            entries.push({
              entityId,
              recordId: TypeAsserter.validate(
                rawRecordId,
                ents.core.dms.data.Record.Id.assertIsId,
              ),
            })
          }
        }
        return entries
      }

      // Dimension single: { '!campus': '?1', '!building': '?2' }
      // Only include the leaf entity — ancestors cover the rest.
      if (TypeChecker.isObject(value)) {
        const rawIds = Object.entries(value).filter(
          (pair): pair is [string, string] => TypeChecker.isString(pair[1]),
        )
        if (!rawIds.length) {
          return []
        }

        const firstPartialId = TypeAsserter.validate(
          rawIds[0][0],
          PartialId.assertIsPartialId,
        )
        for (const dimension of schema.dimensions) {
          const context = ents.core.dms.models.Entity.Id.Context.from(dimension)
          if (!schema.tryGetEntity({ context, partialId: firstPartialId })) {
            continue
          }
          const leaf = schema.tryGetLeafEntityOf(dimension)
          if (!leaf) {
            break
          }
          const leafPartialId = ents.core.dms.models.Id.Parts.from(
            leaf.id,
          ).partialId
          const leafEntry = rawIds.find(([key]) => key === leafPartialId)
          if (!leafEntry) {
            break
          }
          return [
            {
              entityId: leaf.id,
              recordId: TypeAsserter.validate(
                leafEntry[1],
                ents.core.dms.data.Record.Id.assertIsId,
              ),
            },
          ]
        }
        return []
      }

      return []
    },
  )

  /**
   * `true` when any referenced record is still unresolved and its entity
   * is actively loading.
   */
  public readonly isLoadingSg = ngCore.computed(
    (entries = this._resolvedEntriesSg()) => {
      for (const entry of entries) {
        const entityId = entry.entityId as ents.core.dms.models.Entity.Id
        const records = this._referenceRecordsFeature.selectors
          .getRecords({ entityId })
          .asSignal()
        const resolved = records?.some(r => r.id === entry.recordId)
        if (!resolved) {
          const loading = this._referenceRecordsFeature.selectors
            .isLoading({ entityId })
            .asSignal()
          if (loading) {
            return true
          }
        }
      }
      return false
    },
  )

  /**
   * Resolved display labels for the template.
   *
   * Uses each record's ancestors to build a breadcrumb label
   * (e.g. `"Campus ▸ Building ▸ Room"`).
   */
  public readonly resolvedDisplaySg = ngCore.computed(
    (entries = this._resolvedEntriesSg()): readonly string[] =>
      entries
        .map(entry => {
          const entityId = entry.entityId as ents.core.dms.models.Entity.Id
          const record = this._referenceRecordsFeature.selectors
            .getRecords({ entityId })
            .asSignal()
            ?.find(r => r.id === entry.recordId)

          return record
            ? state.display.referenceRecords.models.Record.toBreadcrumb(record)
            : undefined
        })
        .filter(TypeChecker.isNotNullish.raw),
  )

  constructor() {
    ngCore.effect(() => {
      const entries = this._resolvedEntriesSg()

      // Group by entityId for batched dispatch
      const byEntity = new Map<
        ents.core.dms.models.Entity.Id,
        ents.core.dms.data.Record.Id[]
      >()
      for (const entry of entries) {
        const entityId = entry.entityId as ents.core.dms.models.Entity.Id
        let ids = byEntity.get(entityId)
        if (!ids) {
          ids = []
          byEntity.set(entityId, ids)
        }
        ids.push(entry.recordId)
      }

      for (const [entityId, recordIds] of byEntity) {
        this._referenceRecordsFeature.actions.loadRecords
          .from('ReferenceReadonlyValue')
          .dispatch({ cacheFirst: true, entityId, recordIds })
      }
    })
  }

  /**
   * Resolves a raw entity identifier (PartialId or Entity.Id) to a full
   * `Entity.Id` via the schema.
   */
  private _resolveEntityId(
    schema: ents.core.dms.models.Schema,
    rawId: string,
  ): ents.core.dms.models.Entity.Id | undefined {
    if (ents.core.dms.models.Entity.Id.isId(rawId)) {
      return rawId
    }
    if (!PartialId.isPartialId(rawId)) {
      return undefined
    }
    for (const dimension of schema.dimensions) {
      const context = ents.core.dms.models.Entity.Id.Context.from(dimension)
      const entity = schema.tryGetEntity({ context, partialId: rawId })
      if (entity) {
        return entity.id
      }
    }
    return undefined
  }
}
